import { useEffect, useMemo, useRef, useState, type FormEvent, type PointerEvent as ReactPointerEvent } from "react";
import { api, payload } from "./lib/api";
import PendingRequests, { upcomingPending } from "./PendingRequests";
import { businessDateOf } from "./lib/business-day";
import { launchMetaWhatsappSignup } from "./lib/meta-whatsapp";
import type {
  BookingStatus,
  RestaurantBooking,
  RestaurantCustomer,
  RestaurantMessagingConfig,
  RestaurantOperationsConfig,
  RestaurantRoom,
  RestaurantTable,
  TelegramRecipientCandidate,
  WhatsappCoexistenceStatus,
  WhatsappIntegrationStatus,
} from "./types";

const defaultMessagingConfig: RestaurantMessagingConfig = {
  telegramEnabled: false,
  telegramBotTokenConfigured: false,
  telegramChatIds: [],
  telegramLastTestAt: null,
  whatsappEnabled: false,
  whatsappAccessTokenConfigured: false,
  whatsappPhoneNumberId: "",
  whatsappTemplateReceived: "booking_received",
  whatsappTemplateConfirmed: "booking_confirmed",
  whatsappTemplateCancelled: "booking_cancelled",
  whatsappTemplateWaitlisted: "booking_waitlisted",
  whatsappTemplateReminder: "booking_reminder",
  whatsappReminderEnabled: false,
  whatsappReminderHours: 3,
  whatsappTemplateLanguage: "it",
  whatsappManageButtonEnabled: false,
  whatsappLastTestAt: null,
  updatedAt: null,
};

const defaultWhatsappIntegration: WhatsappIntegrationStatus = {
  configured: false,
  connected: false,
  canManage: false,
  appId: "",
  configId: "",
  wabaId: "",
  phoneNumberId: "",
  displayPhoneNumber: "",
  verifiedName: "",
  updatedAt: null,
};

const weekdays = ["Domenica", "Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato"];
const statusLabels: Record<BookingStatus, string> = {
  pending: "Da confermare",
  confirmed: "Confermata",
  seated: "Al tavolo",
  completed: "Completata",
  cancelled: "Cancellata",
  no_show: "No-show",
  waitlisted: "Lista d’attesa",
};

const defaultConfig: RestaurantOperationsConfig = {
  settings: {
    bookingEnabled: false,
    timezone: "Europe/Rome",
    publicTitle: "Prenota il tuo tavolo",
    publicDescription: "Scegli giorno, orario e numero di persone.",
    restaurantPhone: "",
    minPartySize: 1,
    maxPartySize: 12,
    totalCapacity: 40,
    slotIntervalMinutes: 30,
    defaultDurationMinutes: 120,
    minimumNoticeMinutes: 120,
    bookingHorizonDays: 60,
    confirmationMode: "manual",
    maxBookingsPerSlot: 30,
    privacyUrl: "",
    whatsappMode: "customer_initiated",
    googleReserveStatus: "not_started",
    nightCutoffEnabled: true,
    nightCutoffHour: 4,
  },
  hours: weekdays.map((_, weekday) => ({
    id: `default-hours-${weekday}`,
    weekday,
    serviceName: "Cena",
    openTime: "19:00",
    closeTime: "23:00",
    slotIntervalMinutes: 30,
    durationMinutes: 120,
    maxCoversPerSlot: 40,
    maxBookingsPerSlot: 30,
    enabled: false,
  })),
  rooms: [{ id: "room-main", name: "Sala principale", width: 1000, height: 700, active: true, tables: [] }],
};

function localDate() {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function openNativePicker(input: HTMLInputElement) {
  try { input.showPicker?.(); } catch { input.focus(); }
}

type OperationsTab = "agenda" | "floor" | "customers" | "marketing" | "settings";
type RestaurantService = "Aperitivo" | "Cena" | "Dopocena";
const restaurantServices: RestaurantService[] = ["Aperitivo", "Cena", "Dopocena"];

function serviceFromBooking(booking: Pick<RestaurantBooking, "serviceName" | "time">): RestaurantService {
  const saved = booking.serviceName.toLowerCase();
  if (saved.includes("aperitivo")) return "Aperitivo";
  if (saved === "dopocena" || saved === "dopo cena") return "Dopocena";
  if (saved === "cena") return "Cena";
  const hour = Number(booking.time.slice(0, 2));
  if (hour >= 23 || hour < 6) return "Dopocena";
  if (hour >= 20) return "Cena";
  return "Aperitivo";
}

export default function RestaurantOperations({ siteId, canEdit, initialTab = "agenda", initialDate }: { siteId: string; canEdit: boolean; initialTab?: OperationsTab; initialDate?: string }) {
  const [tab, setTab] = useState<OperationsTab>(initialTab);
  const [config, setConfig] = useState<RestaurantOperationsConfig>(defaultConfig);
  const [date, setDate] = useState(() => initialDate || localDate());
  const [bookings, setBookings] = useState<RestaurantBooking[]>([]);
  const [customers, setCustomers] = useState<RestaurantCustomer[]>([]);
  const [customerSummary, setCustomerSummary] = useState({ total: 0, marketingConsent: 0, whatsappConsent: 0, returning: 0 });
  const [roomId, setRoomId] = useState("");
  const [selectedTableId, setSelectedTableId] = useState("");
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState("");
  const [pendingAll, setPendingAll] = useState<RestaurantBooking[]>([]);
  const [pendingOpen, setPendingOpen] = useState(false);
  const [highlightId, setHighlightId] = useState("");
  const [error, setError] = useState("");
  const [messagingConfig, setMessagingConfig] = useState<RestaurantMessagingConfig>(defaultMessagingConfig);
  const [whatsappIntegration, setWhatsappIntegration] = useState<WhatsappIntegrationStatus>(defaultWhatsappIntegration);
  const [messagingSecrets, setMessagingSecrets] = useState({ telegramBotToken: "", whatsappAccessToken: "" });
  const [telegramCandidates, setTelegramCandidates] = useState<TelegramRecipientCandidate[]>([]);
  const [showCancelled, setShowCancelled] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualBooking, setManualBooking] = useState({ firstName: "", lastName: "", phone: "", time: "20:00", partySize: 2, serviceName: "Cena" as RestaurantService, notes: "" });
  const [editingId, setEditingId] = useState("");
  const [editDraft, setEditDraft] = useState({ date: "", time: "", partySize: 2, serviceName: "Cena" as RestaurantService, notes: "" });

  useEffect(() => {
    api<RestaurantOperationsConfig>(`/api/sites/${siteId}/restaurant-operations`)
      .then((result) => {
        setConfig(result);
        setRoomId(result.rooms[0]?.id || "");
      })
      .catch(() => {
        setConfig(defaultConfig);
        setRoomId(defaultConfig.rooms[0].id);
      });
  }, [siteId]);

  useEffect(() => {
    api<RestaurantMessagingConfig>(`/api/sites/${siteId}/restaurant-messaging`)
      .then(setMessagingConfig)
      .catch(() => setMessagingConfig(defaultMessagingConfig));
  }, [siteId]);

  useEffect(() => {
    api<WhatsappIntegrationStatus>(`/api/sites/${siteId}/restaurant-messaging/meta-connection`)
      .then(setWhatsappIntegration)
      .catch(() => setWhatsappIntegration(defaultWhatsappIntegration));
  }, [siteId]);

  useEffect(() => {
    let active = true;
    const refresh = () => api<{ bookings: RestaurantBooking[] }>(`/api/sites/${siteId}/restaurant-bookings?from=${date}&to=${date}&activeOnly=${showCancelled ? "false" : "true"}`)
      .then((result) => { if (active) setBookings(result.bookings); })
      .catch((cause) => { if (active) setError(cause instanceof Error ? cause.message : "Agenda non disponibile"); });
    void refresh();
    const interval = window.setInterval(refresh, 30_000);
    return () => { active = false; window.clearInterval(interval); };
  }, [date, showCancelled, siteId]);

  const loadPending = () => api<{ bookings: RestaurantBooking[] }>(`/api/sites/${siteId}/restaurant-bookings?status=pending`)
    .then((result) => setPendingAll(result.bookings))
    .catch(() => undefined); // rete assente per un attimo: si tiene l'ultima lista buona
  useEffect(() => {
    let active = true;
    const refresh = () => api<{ bookings: RestaurantBooking[] }>(`/api/sites/${siteId}/restaurant-bookings?status=pending`)
      .then((result) => { if (active) setPendingAll(result.bookings); })
      .catch(() => undefined);
    void refresh();
    const interval = window.setInterval(refresh, 30_000);
    return () => { active = false; window.clearInterval(interval); };
  }, [siteId]);
  useEffect(() => {
    if (!highlightId) return;
    const node = document.getElementById(`booking-${highlightId}`);
    if (node) {
      node.scrollIntoView({ behavior: "smooth", block: "center" });
      const timer = window.setTimeout(() => setHighlightId(""), 4000);
      return () => window.clearTimeout(timer);
    }
  }, [highlightId, bookings]);

  const [customersVersion, setCustomersVersion] = useState(0);
  function refreshCustomers() { setCustomersVersion((current) => current + 1); }
  useEffect(() => {
    // Caricata sempre, non solo nella scheda Clienti: serve per riconoscere in agenda e nel
    // form di inserimento manuale i numeri di telefono che hanno già una storia di prenotazioni.
    // refreshCustomers() dopo una prenotazione manuale o un cambio di stato fa ripartire questo
    // effetto così i contatori restano aggiornati senza chiamare setState fuori da un effect.
    let active = true;
    api<{ customers: RestaurantCustomer[]; summary: typeof customerSummary }>(`/api/sites/${siteId}/restaurant-customers`)
      .then((result) => {
        if (!active) return;
        setCustomers(result.customers);
        setCustomerSummary(result.summary);
      })
      .catch((cause) => { if (active) setError(cause instanceof Error ? cause.message : "Clienti non disponibili"); });
    return () => { active = false; };
  }, [siteId, tab, customersVersion]);

  function normalizePhoneKey(value: string): string {
    const digits = value.replace(/\D/g, "");
    if (!digits) return "";
    const withoutCountry = digits.startsWith("00") ? digits.slice(2) : digits.startsWith("39") && digits.length > 10 ? digits.slice(2) : digits;
    return withoutCountry.replace(/^0+/, "").slice(-9);
  }
  const customersByPhone = useMemo(() => {
    const map = new Map<string, RestaurantCustomer>();
    customers.forEach((customer) => {
      const key = normalizePhoneKey(customer.phone);
      if (key) map.set(key, customer);
    });
    return map;
  }, [customers]);
  function findCustomerByPhone(value: string): RestaurantCustomer | undefined {
    const key = normalizePhoneKey(value);
    return key.length >= 6 ? customersByPhone.get(key) : undefined;
  }
  const manualPhoneMatch = useMemo(() => {
    const key = normalizePhoneKey(manualBooking.phone);
    return key.length >= 6 ? customersByPhone.get(key) : undefined;
  }, [manualBooking.phone, customersByPhone]);

  const room = config.rooms.find((item) => item.id === roomId) || config.rooms[0];
  const selectedTable = room?.tables.find((table) => table.id === selectedTableId) || null;
  const activeBookings = bookings.filter((booking) => ["pending", "confirmed", "seated"].includes(booking.status));
  const totalCovers = activeBookings.reduce((sum, booking) => sum + booking.partySize, 0);
  const assigned = activeBookings.filter((booking) => booking.tableIds.length).length;
  const pendingUpcoming = useMemo(() => upcomingPending(pendingAll), [pendingAll]);
  // una richiesta nuova apre da sola il pannello: lo staff la vede senza cercarla nel giorno giusto
  // (si confrontano gli id, non il numero: una confermata e una nuova nello stesso giro lascerebbero il numero uguale)
  const seenPendingIds = useRef(new Set<string>());
  useEffect(() => {
    const nuove = pendingUpcoming.filter((booking) => !seenPendingIds.current.has(booking.id));
    if (!nuove.length) return;
    nuove.forEach((booking) => seenPendingIds.current.add(booking.id));
    setPendingOpen(true);
  }, [pendingUpcoming]);
  const pendingCount = bookings.filter((booking) => booking.status === "pending").length;
  const waitlistedCount = bookings.filter((booking) => booking.status === "waitlisted").length;
  const confirmedCount = bookings.filter((booking) => booking.status === "confirmed").length;
  const pendingCovers = bookings.filter((booking) => booking.status === "pending").reduce((total, booking) => total + booking.partySize, 0);
  const confirmedCovers = bookings.filter((booking) => booking.status === "confirmed").reduce((total, booking) => total + booking.partySize, 0);
  const serviceGroups = restaurantServices.map((serviceName) => {
    const items = bookings
      .filter((booking) => serviceFromBooking(booking) === serviceName)
      .sort((a, b) => a.time.localeCompare(b.time));
    const activeItems = items.filter((booking) => ["pending", "confirmed", "seated"].includes(booking.status));
    return { serviceName, items, covers: activeItems.reduce((total, booking) => total + booking.partySize, 0) };
  });

  async function loadBookings() {
    try {
      const result = await api<{ bookings: RestaurantBooking[] }>(`/api/sites/${siteId}/restaurant-bookings?from=${date}&to=${date}&activeOnly=${showCancelled ? "false" : "true"}`);
      setBookings(result.bookings);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Agenda non disponibile");
    }
  }

  function moveDay(amount: number) {
    const next = new Date(`${date}T12:00:00Z`);
    next.setUTCDate(next.getUTCDate() + amount);
    setDate(next.toISOString().slice(0, 10));
  }

  async function createManualBooking(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canEdit) return;
    setBusy("manual-booking");
    setError("");
    setNotice("");
    try {
      const result = await api<{ booking: RestaurantBooking }>(
        `/api/sites/${siteId}/restaurant-bookings`,
        payload("POST", { ...manualBooking, date }),
      );
      await loadBookings();
      refreshCustomers();
      setManualBooking({ firstName: "", lastName: "", phone: "", time: manualBooking.time, partySize: 2, serviceName: manualBooking.serviceName, notes: "" });
      setManualOpen(false);
      setNotice(`Prenotazione ${result.booking.reference} inserita in agenda.`);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Prenotazione non inserita");
    } finally {
      setBusy("");
    }
  }

  async function saveConfig(next = config) {
    if (!canEdit) return;
    setBusy("config");
    setError("");
    try {
      const result = await api<RestaurantOperationsConfig & { saved: true }>(
        `/api/sites/${siteId}/restaurant-operations`,
        payload("PUT", next),
      );
      setConfig({ settings: result.settings, hours: result.hours, rooms: result.rooms });
      setNotice("Configurazione salvata");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Salvataggio non riuscito");
    } finally {
      setBusy("");
    }
  }

  async function confirmPending(booking: RestaurantBooking) {
    await updateBooking(booking, { status: "confirmed" });
    await loadPending();
  }
  async function rejectPending(booking: RestaurantBooking) {
    if (!window.confirm(`Rifiutare la richiesta di ${booking.customer.firstName} ${booking.customer.lastName} per il ${new Date(`${booking.date}T12:00:00`).toLocaleDateString("it-IT", { day: "numeric", month: "long" })}? I coperti vengono liberati.`)) return;
    await updateBooking(booking, { status: "cancelled" });
    await loadPending();
  }
  function goToBookingDay(booking: RestaurantBooking) {
    setDate(businessDateOf(booking.date, booking.time));
    setPendingOpen(false);
    setHighlightId(booking.id);
  }

  async function updateBooking(booking: RestaurantBooking, update: { status?: BookingStatus; tableIds?: string[]; notes?: string; date?: string; time?: string; partySize?: number; serviceName?: RestaurantService }) {
    if (!canEdit) return;
    setBusy(booking.id);
    setError("");
    setNotice("");
    try {
      const result = await api<{ booking: RestaurantBooking }>(
        `/api/sites/${siteId}/restaurant-bookings/${booking.id}`,
        payload("PATCH", update),
      );
      if (update.status) void loadPending(); // il contatore «da confermare» resta allineato all'agenda
      const movedToOtherDay = Boolean(update.date && update.date !== booking.date);
      if (movedToOtherDay) {
        setBookings((current) => current.filter((item) => item.id !== booking.id));
      } else {
        setBookings((current) => current.map((item) => item.id === booking.id ? result.booking : item));
      }
      if (update.status) refreshCustomers();
      if (update.status === "confirmed") setNotice(`Prenotazione ${booking.reference} confermata correttamente.`);
      if (update.status === "cancelled") {
        if (!showCancelled) setBookings((current) => current.filter((item) => item.id !== booking.id));
        setNotice(`Prenotazione ${booking.reference} cancellata. I coperti sono stati liberati.`);
      }
      if (update.date || update.time) {
        const dateLabel = new Intl.DateTimeFormat("it-IT", { day: "numeric", month: "long", timeZone: "UTC" }).format(new Date(`${result.booking.date}T12:00:00Z`));
        setNotice(`Prenotazione ${booking.reference} aggiornata: ${dateLabel} alle ${result.booking.time}.`);
      } else if (update.partySize !== undefined || update.serviceName !== undefined || update.notes !== undefined) {
        setNotice(`Prenotazione ${booking.reference} aggiornata.`);
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Aggiornamento non riuscito");
    } finally {
      setBusy("");
    }
  }

  function cancelBooking(booking: RestaurantBooking) {
    if (!canEdit || busy === booking.id) return;
    const confirmed = window.confirm(`Cancellare la prenotazione ${booking.reference} delle ${booking.time}? I coperti verranno liberati.`);
    if (confirmed) void updateBooking(booking, { status: "cancelled" });
  }

  async function saveMessagingConfig() {
    if (!canEdit) return;
    setBusy("messaging-config");
    setError("");
    try {
      const result = await api<RestaurantMessagingConfig & { saved: true }>(
        `/api/sites/${siteId}/restaurant-messaging`,
        payload("PUT", { ...messagingConfig, ...messagingSecrets }),
      );
      setMessagingConfig(result);
      setMessagingSecrets({ telegramBotToken: "", whatsappAccessToken: "" });
      setNotice("Notifiche e WhatsApp salvati");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Configurazione messaggi non salvata");
    } finally {
      setBusy("");
    }
  }

  async function connectWhatsappWithMeta() {
    if (!canEdit || !whatsappIntegration.configured) return;
    setBusy("whatsapp-connect");
    setError("");
    setNotice("");
    try {
      const { code, session } = await launchMetaWhatsappSignup(whatsappIntegration.appId, whatsappIntegration.configId);
      const result = await api<{
        connected: true;
        wabaId: string;
        phoneNumberId: string;
        displayPhoneNumber: string;
        verifiedName: string;
        onboarding: "cloud_api" | "coexistence";
        coexistence: WhatsappCoexistenceStatus | null;
        webhooksSubscribed: boolean;
        warning: string | null;
        updatedAt: string;
      }>(`/api/sites/${siteId}/restaurant-messaging/meta-connection`, payload("POST", { code, ...session }));
      setWhatsappIntegration((current) => ({ ...current, ...result }));
      setMessagingConfig((current) => ({
        ...current,
        whatsappAccessTokenConfigured: true,
        whatsappPhoneNumberId: result.phoneNumberId,
      }));
      const label = result.displayPhoneNumber || result.verifiedName || "WhatsApp Business";
      if (result.warning) {
        setError(`${label} collegato, ma: ${result.warning}. Usa «Riprova» qui sotto.`);
      } else if (result.coexistence && !result.coexistence.syncComplete) {
        setError(`${label} collegato, ma la sincronizzazione con l'app non è completa: ${result.coexistence.lastError || "riprova"}. Va completata entro 24 ore.`);
      } else {
        setNotice(result.onboarding === "coexistence"
          ? `${label} collegato insieme all'app WhatsApp Business. Tieni l'app aperta sul telefono qualche minuto per la sincronizzazione, poi ricollega WhatsApp Web sui computer.`
          : `${label} collegato correttamente. Ora verifica i template prima di attivare gli invii automatici.`);
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Collegamento Meta non riuscito");
    } finally {
      setBusy("");
    }
  }

  async function retryWhatsappSync() {
    if (!canEdit) return;
    setBusy("whatsapp-sync");
    setError("");
    setNotice("");
    try {
      const result = await api<{ coexistence: WhatsappCoexistenceStatus | null; webhooksSubscribed: boolean; warning: string | null; updatedAt: string }>(
        `/api/sites/${siteId}/restaurant-messaging/meta-connection/sync`,
        payload("POST", {}),
      );
      setWhatsappIntegration((current) => ({ ...current, coexistence: result.coexistence, webhooksSubscribed: result.webhooksSubscribed, updatedAt: result.updatedAt }));
      const problem = result.warning || (result.coexistence && !result.coexistence.syncComplete ? result.coexistence.lastError || "Sincronizzazione non completata" : "");
      if (problem) setError(problem);
      else setNotice("Passaggi con Meta completati.");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Sincronizzazione non riuscita");
    } finally {
      setBusy("");
    }
  }

  async function sendTelegramTest() {
    if (!canEdit) return;
    setBusy("telegram-test");
    setError("");
    try {
      const result = await api<{ sent: true; lastTestAt: string }>(
        `/api/sites/${siteId}/restaurant-messaging/telegram-test`,
        payload("POST", {}),
      );
      setMessagingConfig((current) => ({ ...current, telegramLastTestAt: result.lastTestAt }));
      setNotice("Notifica Telegram di prova inviata");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Notifica Telegram non inviata");
    } finally {
      setBusy("");
    }
  }

  async function discoverTelegramRecipients() {
    if (!canEdit) return;
    setBusy("telegram-discover");
    setError("");
    try {
      const result = await api<{ recipients: TelegramRecipientCandidate[] }>(
        `/api/sites/${siteId}/restaurant-messaging/telegram-recipients`,
      );
      setTelegramCandidates(result.recipients);
      if (!result.recipients.length) setNotice("Nessun nuovo messaggio trovato. Chiedi al contatto di scrivere al bot e riprova.");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Contatti Telegram non disponibili");
    } finally {
      setBusy("");
    }
  }

  async function registerTelegramRecipient(candidate: TelegramRecipientCandidate) {
    if (!canEdit || candidate.configured) return;
    setBusy(`telegram-register-${candidate.chatId}`);
    setError("");
    try {
      const result = await api<RestaurantMessagingConfig & { registered: true; recipient: TelegramRecipientCandidate }>(
        `/api/sites/${siteId}/restaurant-messaging/telegram-recipients`,
        payload("POST", { chatId: candidate.chatId }),
      );
      setMessagingConfig(result);
      setTelegramCandidates((current) => current.map((item) => item.chatId === candidate.chatId ? { ...item, configured: true } : item));
      setNotice(`${candidate.name} riceverà le notifiche delle nuove prenotazioni.`);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Contatto Telegram non registrato");
    } finally {
      setBusy("");
    }
  }

  async function testWhatsappConnection() {
    if (!canEdit) return;
    setBusy("whatsapp-test");
    setError("");
    try {
      const result = await api<{ connected: true; displayPhoneNumber: string; verifiedName: string; lastTestAt: string }>(
        `/api/sites/${siteId}/restaurant-messaging/whatsapp-test`, payload("POST", {}),
      );
      setMessagingConfig((current) => ({ ...current, whatsappLastTestAt: result.lastTestAt }));
      setNotice(`WhatsApp collegato${result.displayPhoneNumber ? `: ${result.displayPhoneNumber}` : ""}`);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Connessione WhatsApp non riuscita");
    } finally {
      setBusy("");
    }
  }

  async function resendWhatsapp(booking: RestaurantBooking) {
    if (!canEdit) return;
    setBusy(`whatsapp-${booking.id}`);
    setError("");
    try {
      await api(`/api/sites/${siteId}/restaurant-bookings/${booking.id}/resend-whatsapp`, payload("POST", {}));
      const result = await api<{ bookings: RestaurantBooking[] }>(`/api/sites/${siteId}/restaurant-bookings?from=${date}&to=${date}&activeOnly=${showCancelled ? "false" : "true"}`);
      setBookings(result.bookings);
      setNotice("Conferma WhatsApp elaborata");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Conferma WhatsApp non inviata");
    } finally {
      setBusy("");
    }
  }

  function updateRoom(nextRoom: RestaurantRoom) {
    setConfig((current) => ({ ...current, rooms: current.rooms.map((item) => item.id === nextRoom.id ? nextRoom : item) }));
  }

  function addTable() {
    if (!room) return;
    const number = room.tables.length + 1;
    const id = `table-${crypto.randomUUID()}`;
    updateRoom({
      ...room,
      tables: [...room.tables, {
        id,
        label: `T${number}`,
        seats: 4,
        minPartySize: 1,
        x: 40 + (number % 6) * 130,
        y: 40 + Math.floor(number / 6) * 130,
        width: 88,
        height: 88,
        shape: "round",
        combinable: false,
        active: true,
      }],
    });
    setSelectedTableId(id);
  }

  function addRoom() {
    const nextNumber = config.rooms.length + 1;
    const nextRoom: RestaurantRoom = {
      id: `room-${crypto.randomUUID()}`,
      name: `Sala ${nextNumber}`,
      width: 1000,
      height: 700,
      active: true,
      tables: [],
    };
    setConfig((current) => ({ ...current, rooms: [...current.rooms, nextRoom] }));
    setRoomId(nextRoom.id);
    setSelectedTableId("");
  }

  function updateSelectedTable(update: Partial<RestaurantTable>) {
    if (!room || !selectedTable) return;
    const next = { ...selectedTable, ...update };
    next.x = Math.max(0, Math.min(next.x, room.width - next.width));
    next.y = Math.max(0, Math.min(next.y, room.height - next.height));
    updateRoom({ ...room, tables: room.tables.map((table) => table.id === selectedTable.id ? next : table) });
  }

  function applyTablePreset(preset: "small-round" | "large-round" | "square" | "rectangle") {
    if (preset === "small-round") updateSelectedTable({ shape: "round", width: 76, height: 76 });
    if (preset === "large-round") updateSelectedTable({ shape: "round", width: 132, height: 132 });
    if (preset === "square") updateSelectedTable({ shape: "square", width: 96, height: 96 });
    if (preset === "rectangle") updateSelectedTable({ shape: "rectangle", width: 168, height: 96 });
  }

  function moveTable(table: RestaurantTable, event: ReactPointerEvent<HTMLButtonElement>) {
    if (!canEdit || !room) return;
    setSelectedTableId(table.id);
    const board = event.currentTarget.parentElement?.getBoundingClientRect();
    if (!board) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    const startX = event.clientX;
    const startY = event.clientY;
    const originX = table.x;
    const originY = table.y;
    const scaleX = room.width / board.width;
    const scaleY = room.height / board.height;
    function move(pointer: PointerEvent) {
      const x = Math.max(0, Math.min(room.width - table.width, Math.round(originX + (pointer.clientX - startX) * scaleX)));
      const y = Math.max(0, Math.min(room.height - table.height, Math.round(originY + (pointer.clientY - startY) * scaleY)));
      updateRoom({ ...room, tables: room.tables.map((item) => item.id === table.id ? { ...item, x, y } : item) });
    }
    function stop() {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", stop);
    }
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", stop, { once: true });
  }

  const marketingCustomers = useMemo(() => customers.filter((customer) => customer.marketingConsent), [customers]);
  function exportContacts(list: RestaurantCustomer[]) {
    if (!canEdit) return;
    const rows = [["Nome", "Cognome", "Telefono", "Email", "Visite", "Ultima visita"], ...list.map((customer) => [
      customer.firstName, customer.lastName, customer.phone, customer.email, String(customer.visitsCount), customer.lastVisitAt || "",
    ])];
    const csv = rows.map((row) => row.map((value) => `"${value.replaceAll('"', '""')}"`).join(",")).join("\n");
    const link = document.createElement("a");
    link.href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    link.download = `contatti-${date}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  }

  function whatsappState(booking: RestaurantBooking) {
    if (!booking.whatsappConsent) return { tone: "waiting", label: "WhatsApp non disponibile" };
    const notification = booking.whatsappNotification;
    if (notification?.deliveryStatus === "sent" || notification?.deliveryStatus === "delivered") return { tone: "delivered", label: "WhatsApp inviato" };
    if (notification?.deliveryStatus === "failed") return { tone: "failed", label: "WhatsApp non inviato" };
    if (notification?.deliveryStatus === "not_configured") return { tone: "failed", label: "WhatsApp da configurare" };
    if (["queued", "sending"].includes(notification?.status || "")) return { tone: "sending", label: "WhatsApp in invio" };
    return { tone: "waiting", label: booking.status === "confirmed" ? "WhatsApp da inviare" : "WhatsApp dopo conferma" };
  }

  return (
    <div className="restaurant-ops">
      <div className="ops-head">
        <div>
          <p className="eyebrow indigo">Sistema operativo del locale</p>
          <h2>Prenotazioni e Marketing</h2>
          <p>Agenda, sala, clienti e consensi in un unico pannello.</p>
        </div>
        <span className="ops-live"><i /> Aggiornamento automatico</span>
      </div>
      <nav className="ops-tabs" aria-label="Gestione ristorante">
        {([
          ["agenda", "Agenda"], ["floor", "Mappa tavoli"], ["customers", "Clienti"],
          ["marketing", "Marketing"], ["settings", "Impostazioni"],
        ] as const).map(([value, label]) => (
          <button key={value} className={tab === value ? "active" : ""} onClick={() => setTab(value)}>{label}</button>
        ))}
      </nav>
      {error && <div className="notice error">{error}</div>}
      {notice && <div className="notice success">{notice}</div>}

      {tab === "agenda" && <>
        <div className="agenda-command">
          <div className="agenda-command-top">
            <div className="agenda-date-control">
              <button type="button" aria-label="Giorno precedente" onClick={() => moveDay(-1)}>‹</button>
              <label>Giorno<input className="picker-field" type="date" value={date} onClick={(event) => openNativePicker(event.currentTarget)} onChange={(event) => setDate(event.target.value)} /></label>
              <button type="button" aria-label="Giorno successivo" onClick={() => moveDay(1)}>›</button>
            </div>
            <button className="manual-booking-trigger" type="button" aria-expanded={manualOpen} onClick={() => setManualOpen((current) => !current)}>{manualOpen ? "Chiudi" : "+ Aggiungi prenotazione"}</button>
          </div>
          <div className="agenda-totals agenda-totals-primary" title={`${totalCovers} coperti operativi · ${assigned} con tavolo assegnato · ${waitlistedCount} in lista d'attesa`}>
            <span className="total-confirmed"><small>Prenotazioni giornaliere</small><strong>{confirmedCovers}</strong><b>coperti · {confirmedCount} {confirmedCount === 1 ? "prenotazione" : "prenotazioni"} confermate</b></span>
            <span className={`total-pending is-clickable${pendingOpen ? " is-open" : ""}${pendingUpcoming.length ? " has-requests" : ""}`} role="button" tabIndex={0} aria-expanded={pendingOpen} title="Tocca per vedere le richieste e confermarle" onClick={() => setPendingOpen((current) => !current)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); setPendingOpen((current) => !current); } }}><small>Richieste giornaliere</small><strong>{pendingCovers}</strong><b>coperti · {pendingCount} {pendingCount === 1 ? "prenotazione" : "prenotazioni"} da confermare</b><i className="pending-hint">{pendingUpcoming.length ? `${pendingUpcoming.length} da confermare in tutto · ${pendingOpen ? "chiudi" : "tocca per aprire"}` : "nessuna richiesta da confermare"}</i></span>
          </div>
        </div>
        {pendingOpen && <PendingRequests bookings={pendingUpcoming} canEdit={canEdit} busyId={busy} selectedDate={date} onConfirm={(booking) => void confirmPending(booking)} onReject={(booking) => void rejectPending(booking)} onGoToDay={goToBookingDay} onClose={() => setPendingOpen(false)} />}
        {manualOpen && <form className="manual-booking-form" onSubmit={createManualBooking}>
          <div className="manual-form-head"><div><h3>Nuova prenotazione</h3><p>Inserimento telefonico per il {new Intl.DateTimeFormat("it-IT", { weekday: "long", day: "numeric", month: "long", timeZone: "UTC" }).format(new Date(`${date}T12:00:00Z`))}.</p></div><span>Confermata</span></div>
          <div className="manual-form-grid">
            <label>Nome<input autoFocus required maxLength={80} autoComplete="given-name" value={manualBooking.firstName} onChange={(event) => setManualBooking({ ...manualBooking, firstName: event.target.value })} /></label>
            <label>Cognome<input required maxLength={80} autoComplete="family-name" value={manualBooking.lastName} onChange={(event) => setManualBooking({ ...manualBooking, lastName: event.target.value })} /></label>
            <label>Ora<input className="picker-field" required type="time" value={manualBooking.time} onClick={(event) => openNativePicker(event.currentTarget)} onChange={(event) => setManualBooking({ ...manualBooking, time: event.target.value })} /></label>
            <label>Posti<input required type="number" inputMode="numeric" min="1" max={config.settings.totalCapacity} value={manualBooking.partySize} onChange={(event) => setManualBooking({ ...manualBooking, partySize: Number(event.target.value) })} /></label>
            <label className="manual-service">Servizio<select value={manualBooking.serviceName} onChange={(event) => setManualBooking({ ...manualBooking, serviceName: event.target.value as RestaurantService })}>{restaurantServices.map((service) => <option key={service}>{service}</option>)}</select></label>
            <label className="manual-phone">Numero di telefono<input required type="tel" inputMode="tel" autoComplete="tel" placeholder="349 123 4567" value={manualBooking.phone} onChange={(event) => setManualBooking({ ...manualBooking, phone: event.target.value })} /></label>
            {manualPhoneMatch && <div className="phone-history">
              <strong>{manualPhoneMatch.firstName} {manualPhoneMatch.lastName}</strong> è già in anagrafica
              {manualPhoneMatch.visitsCount > 0 && <> · {manualPhoneMatch.visitsCount} {manualPhoneMatch.visitsCount === 1 ? "visita completata" : "visite completate"}</>}
              {manualPhoneMatch.lastVisitAt && <> · ultima il {new Intl.DateTimeFormat("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(manualPhoneMatch.lastVisitAt))}</>}
              {manualPhoneMatch.noShowCount > 0 && <span className="phone-history-noshow"> · {manualPhoneMatch.noShowCount} no-show</span>}
              <button type="button" onClick={() => setManualBooking({ ...manualBooking, firstName: manualPhoneMatch.firstName, lastName: manualPhoneMatch.lastName })}>Usa nome salvato</button>
            </div>}
            <label className="manual-notes">Note<textarea maxLength={1200} placeholder="Compleanno, seggiolone, tavolo preferito…" value={manualBooking.notes} onChange={(event) => setManualBooking({ ...manualBooking, notes: event.target.value })} /></label>
          </div>
          <button className="manual-save" disabled={!canEdit || busy === "manual-booking"}>{busy === "manual-booking" ? "Inserimento…" : "Inserisci in agenda"}</button>
        </form>}
        <div className="agenda-secondary-row"><button className="cancelled-toggle" type="button" aria-pressed={showCancelled} onClick={() => setShowCancelled((current) => !current)}>{showCancelled ? "Nascondi cancellate" : "Mostra storico e cancellate"}</button></div>
        <div className="service-overview" aria-label="Coperti per servizio">
          {serviceGroups.map((group) => <article className={`service-overview-${group.serviceName.toLowerCase()}`} key={group.serviceName}><span>{group.serviceName}</span><strong>{group.covers}</strong><small>{group.covers === 1 ? "coperto" : "coperti"}</small></article>)}
        </div>
        <div className="booking-board">
          {!bookings.length && <div className="ops-empty"><strong>Agenda libera</strong><p>Le richieste online appariranno qui. Puoi anche inserire subito una prenotazione telefonica.</p><button type="button" onClick={() => setManualOpen(true)}>Aggiungi prenotazione</button></div>}
          {serviceGroups.map((group) => group.items.length ? <section className={`service-group service-group-${group.serviceName.toLowerCase()}`} key={group.serviceName}>
            <header><div><span>{group.serviceName}</span><strong>{group.items.length} {group.items.length === 1 ? "prenotazione" : "prenotazioni"}</strong></div><em>{group.covers} {group.covers === 1 ? "coperto" : "coperti"}</em></header>
            <div className="service-bookings">{group.items.map((booking) => {
            const knownCustomer = findCustomerByPhone(booking.customer.phone);
            const isReturning = Boolean(knownCustomer && knownCustomer.visitsCount > 1);
            return <article id={`booking-${booking.id}`} className={`booking-card status-${booking.status}${highlightId === booking.id ? " is-highlight" : ""}`} key={booking.id}>
            <div className="booking-time">
              <span className="booking-field-label">Orario</span>
              <strong>{booking.time}</strong>
              <small>{serviceFromBooking(booking)}</small>
            </div>
            <div className="booking-person">
              <span className="booking-field-label">Nome</span>
              <strong className="booking-name">{booking.customer.firstName} {booking.customer.lastName}</strong>
              {isReturning && <span className="returning-tag" title={`${knownCustomer!.visitsCount} prenotazioni precedenti`}>Cliente abituale · {knownCustomer!.visitsCount}×</span>}
              <div className="booking-main-data">
                <span className="booking-party">
                  <span className="booking-field-label">N. Persone</span>
                  <b>{booking.partySize}</b>
                </span>
                <span className="booking-phone">
                  <span className="booking-field-label">N. Telefono</span>
                  <a href={`tel:${booking.customer.phone}`}>{booking.customer.phone}</a>
                </span>
              </div>
              <small className={`booking-source source-${booking.source}`}>{booking.source === "web" ? "Dal sito" : "Inserita manualmente"}</small>
              <small className="booking-created">Richiesta inviata il {new Intl.DateTimeFormat("it-IT", { timeZone: config.settings.timezone, day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(new Date(booking.createdAt))}</small>
              {(booking.details.occasion || booking.details.notes) && <div className="booking-notes"><strong>Note</strong><p>{[booking.details.occasion, booking.details.notes].filter(Boolean).join(" · ")}</p></div>}
              <span className={`message-state message-${whatsappState(booking).tone}`} title={booking.whatsappNotification?.lastError || undefined}>{whatsappState(booking).label}</span>
            </div>
            <select aria-label={`Stato ${booking.reference}`} value={booking.status} disabled={!canEdit || busy === booking.id} onChange={(event) => updateBooking(booking, { status: event.target.value as BookingStatus })}>
              {Object.entries(statusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
            <select aria-label={`Tavolo ${booking.reference}`} value={booking.tableIds[0] || ""} disabled={!canEdit || busy === booking.id} onChange={(event) => updateBooking(booking, { tableIds: event.target.value ? [event.target.value] : [] })}>
              <option value="">Senza tavolo</option>
              {config.rooms.flatMap((item) => item.tables).map((table) => <option key={table.id} value={table.id}>{table.label} · {table.seats} posti</option>)}
            </select>
            <div className="booking-contact-actions">
              {booking.status === "pending" && <button className="confirm-action" disabled={!canEdit || busy === booking.id} onClick={() => updateBooking(booking, { status: "confirmed" })}>{busy === booking.id ? "Confermo…" : "Conferma prenotazione"}</button>}
              {booking.status === "confirmed" && booking.whatsappConsent && <button className="message-action" disabled={!canEdit || busy === `whatsapp-${booking.id}`} onClick={() => resendWhatsapp(booking)}>{booking.whatsappNotification ? "Reinvia WhatsApp" : "Invia WhatsApp"}</button>}
              {booking.whatsappUrl && <a className="wa-action" href={booking.whatsappUrl} target="_blank" rel="noreferrer">Apri WhatsApp</a>}
              {booking.status !== "cancelled" && <button className="reschedule-action" type="button" disabled={!canEdit || busy === booking.id} onClick={() => {
                if (editingId === booking.id) {
                  setEditingId("");
                  return;
                }
                setEditingId(booking.id);
                setEditDraft({
                  date: booking.date,
                  time: booking.time,
                  partySize: booking.partySize,
                  serviceName: serviceFromBooking(booking),
                  notes: booking.details.notes || "",
                });
              }}>{editingId === booking.id ? "Chiudi modifica" : "Modifica prenotazione"}</button>}
              {booking.status !== "cancelled" && <button className="cancel-action" disabled={!canEdit || busy === booking.id} onClick={() => cancelBooking(booking)}>{busy === booking.id ? "Cancello…" : "Cancella prenotazione"}</button>}
            </div>
            {editingId === booking.id && <div className="booking-edit-editor">
              <header>
                <strong>Modifica prenotazione</strong>
                <small>Giorno, orario, posti, servizio e note in un unico riquadro.</small>
              </header>
              <div className="booking-edit-grid">
                <label>Giorno<input className="picker-field" type="date" value={editDraft.date} onClick={(event) => openNativePicker(event.currentTarget)} onChange={(event) => setEditDraft({ ...editDraft, date: event.target.value })} /></label>
                <label>Orario<input className="picker-field" type="time" value={editDraft.time} onClick={(event) => openNativePicker(event.currentTarget)} onChange={(event) => setEditDraft({ ...editDraft, time: event.target.value })} /></label>
                <label>N. Persone<input type="number" inputMode="numeric" min="1" max={config.settings.totalCapacity} value={editDraft.partySize} onChange={(event) => setEditDraft({ ...editDraft, partySize: Number(event.target.value) })} /></label>
                <label>Servizio<select value={editDraft.serviceName} onChange={(event) => setEditDraft({ ...editDraft, serviceName: event.target.value as RestaurantService })}>{restaurantServices.map((service) => <option key={service}>{service}</option>)}</select></label>
                <label className="booking-edit-notes">Note<textarea maxLength={1200} placeholder="Compleanno, seggiolone, tavolo preferito…" value={editDraft.notes} onChange={(event) => setEditDraft({ ...editDraft, notes: event.target.value })} /></label>
              </div>
              <footer>
                <button type="button" className="secondary" disabled={busy === booking.id} onClick={() => setEditingId("")}>Annulla</button>
                <button type="button" disabled={busy === booking.id} onClick={async () => {
                  await updateBooking(booking, {
                    date: editDraft.date,
                    time: editDraft.time,
                    partySize: editDraft.partySize,
                    serviceName: editDraft.serviceName,
                    notes: editDraft.notes,
                  });
                  setEditingId("");
                }}>{busy === booking.id ? "Salvataggio…" : "Salva tutto"}</button>
              </footer>
            </div>}
            <span className="booking-ref">{booking.reference}</span>
          </article>;
          })}</div></section> : null)}
        </div>
      </>}

      {tab === "floor" && <div className="floor-layout">
        <aside>
          <p>Sale</p>
          {config.rooms.map((item) => <button type="button" key={item.id} className={room?.id === item.id ? "active" : ""} onClick={() => { setRoomId(item.id); setSelectedTableId(""); }}>{item.name}<span>{item.tables.length}</span></button>)}
          <button type="button" className="floor-add-room" onClick={addRoom} disabled={!canEdit}>+ Nuova sala</button>
          {room && <div className="room-editor"><label>Nome della sala<input maxLength={80} value={room.name} disabled={!canEdit} onChange={(event) => updateRoom({ ...room, name: event.target.value })} /></label></div>}
          <button type="button" className="secondary" onClick={addTable} disabled={!canEdit}>+ Aggiungi tavolo</button>
          <button type="button" className="primary" onClick={() => saveConfig()} disabled={!canEdit || busy === "config"}>{busy === "config" ? "Salvataggio…" : "Salva sale e tavoli"}</button>
        </aside>
        {room && <section>
          <div className="floor-legend">
            <strong>{room.name}</strong>
            <span className="floor-help-desktop">Tocca un tavolo per modificarlo. Trascinalo per spostarlo.</span>
            <span className="floor-help-mobile">Tocca un tavolo per modificarlo. Sul telefono i tavoli sono ordinati in griglia.</span>
          </div>
          {selectedTable ? <div className="table-editor">
            <div className="table-editor-fields">
              <label>Nome tavolo<input maxLength={40} value={selectedTable.label} disabled={!canEdit} onChange={(event) => updateSelectedTable({ label: event.target.value })} /></label>
              <label>Coperti<input type="number" inputMode="numeric" min="1" max="1000" value={selectedTable.seats} disabled={!canEdit} onChange={(event) => updateSelectedTable({ seats: Number(event.target.value) })} /></label>
              <label>Forma<select value={selectedTable.shape} disabled={!canEdit} onChange={(event) => updateSelectedTable({ shape: event.target.value as RestaurantTable["shape"] })}><option value="round">Rotondo</option><option value="square">Quadrato</option><option value="rectangle">Rettangolare</option></select></label>
              <label>Larghezza<input type="number" inputMode="numeric" min="48" max="600" value={selectedTable.width} disabled={!canEdit} onChange={(event) => updateSelectedTable({ width: Number(event.target.value) })} /></label>
              <label>Profondità<input type="number" inputMode="numeric" min="48" max="600" value={selectedTable.height} disabled={!canEdit} onChange={(event) => updateSelectedTable({ height: Number(event.target.value) })} /></label>
            </div>
            <div className="table-presets" aria-label="Dimensioni rapide"><span>Modelli rapidi</span><button type="button" onClick={() => applyTablePreset("small-round")}>Tondo piccolo</button><button type="button" onClick={() => applyTablePreset("large-round")}>Tondo grande</button><button type="button" onClick={() => applyTablePreset("square")}>Quadrato</button><button type="button" onClick={() => applyTablePreset("rectangle")}>Rettangolare</button></div>
          </div> : <div className="table-editor-empty">Seleziona un tavolo nella sala per cambiare nome, coperti, forma e dimensioni.</div>}
          <div className="floor-board" style={{ aspectRatio: `${room.width}/${room.height}` }}>
            {room.tables.map((table) => {
              const occupied = bookings.some((booking) => booking.tableIds.includes(table.id) && !["cancelled", "no_show", "completed"].includes(booking.status));
              return <button
                key={table.id}
                type="button"
                aria-label={`${table.label}, ${table.seats} posti. Tocca per modificare.`}
                className={`floor-table shape-${table.shape}${occupied ? " occupied" : ""}${selectedTableId === table.id ? " selected" : ""}`}
                style={{ left: `${table.x / room.width * 100}%`, top: `${table.y / room.height * 100}%`, width: `${table.width / room.width * 100}%`, height: `${table.height / room.height * 100}%` }}
                onPointerDown={(event) => moveTable(table, event)}
              ><strong>{table.label}</strong><small>{table.seats} posti</small></button>;
            })}
          </div>
        </section>}
      </div>}

      {tab === "customers" && <div className="customer-panel">
        <div className="ops-toolbar"><div className="ops-kpi"><strong>{customerSummary.total}</strong><span>clienti</span></div><div className="ops-kpi"><strong>{customerSummary.returning}</strong><span>abituali</span></div><div className="ops-kpi"><strong>{customerSummary.marketingConsent}</strong><span>contattabili</span></div><button className="secondary" disabled={!canEdit} onClick={() => exportContacts(customers)}>Esporta CSV</button></div>
        <div className="customer-table"><div className="customer-row head"><span>Cliente</span><span>Contatti</span><span>Visite</span><span>Consensi</span></div>
          {customers.map((customer) => <div className="customer-row" key={customer.id}><strong>{customer.firstName} {customer.lastName}</strong><span>{customer.phone}<small>{customer.email}</small></span><span>{customer.visitsCount}<small>{customer.totalGuests} coperti</small></span><span className="consent-tags">{customer.whatsappConsent && <i>WhatsApp</i>}{customer.marketingConsent && <i>Marketing</i>}</span></div>)}
        </div>
      </div>}

      {tab === "marketing" && <div className="marketing-panel">
        <div className="marketing-hero"><div><p className="eyebrow">CRM proprietario</p><h3>Trasforma le prenotazioni in relazioni.</h3><p>I contatti entrano nel database con provenienza e consensi separati. Nessun invio viene effettuato a chi non ha accettato il marketing.</p></div><button className="primary" disabled={!canEdit} onClick={() => exportContacts(marketingCustomers)}>Esporta pubblico autorizzato</button></div>
        <div className="segment-grid"><article><span>{customerSummary.marketingConsent}</span><strong>Marketing autorizzato</strong><p>Email e comunicazioni promozionali solo con consenso espresso.</p></article><article><span>{customerSummary.whatsappConsent}</span><strong>WhatsApp informato</strong><p>Numero lasciato nel modulo con l'avviso dei messaggi di servizio, con data e fonte.</p></article><article><span>{customerSummary.returning}</span><strong>Clienti abituali</strong><p>Segmento basato sulle visite completate, non su dati presunti.</p></article></div>
        <div className="marketing-safety"><strong>Regola anti-blocco WhatsApp</strong><p>Il flusso gratuito fa iniziare la conversazione al cliente dopo la prenotazione. Gli invii automatici restano disattivati finché non viene collegata la WhatsApp Business Platform ufficiale con template utility e opt-in verificato.</p></div>
      </div>}

      {tab === "settings" && <div className="ops-settings">
        <section><h3>Regole di prenotazione</h3><label className="booking-enable"><input type="checkbox" checked={config.settings.bookingEnabled} onChange={(event) => setConfig({ ...config, settings: { ...config.settings, bookingEnabled: event.target.checked } })} /><span><strong>Prenotazioni online attive</strong><small>Attivale solo dopo aver verificato orari, capienza, telefono e informativa privacy.</small></span></label><div className="field-pair"><label>Capienza totale<input type="number" min="1" max="5000" value={config.settings.totalCapacity} onChange={(event) => setConfig({ ...config, settings: { ...config.settings, totalCapacity: Number(event.target.value) } })} /></label><label>Massimo online per gruppo<input type="number" min="1" max="1000" value={config.settings.maxPartySize} onChange={(event) => setConfig({ ...config, settings: { ...config.settings, maxPartySize: Number(event.target.value) } })} /></label></div><div className="field-pair"><label>Preavviso minimo, minuti<input type="number" min="0" value={config.settings.minimumNoticeMinutes} onChange={(event) => setConfig({ ...config, settings: { ...config.settings, minimumNoticeMinutes: Number(event.target.value) } })} /></label><label>Giorni prenotabili<input type="number" min="1" max="365" value={config.settings.bookingHorizonDays} onChange={(event) => setConfig({ ...config, settings: { ...config.settings, bookingHorizonDays: Number(event.target.value) } })} /></label></div><label className="booking-enable"><input type="checkbox" checked={config.settings.nightCutoffEnabled} onChange={(event) => setConfig({ ...config, settings: { ...config.settings, nightCutoffEnabled: event.target.checked } })} /><span><strong>Le prenotazioni di notte contano per la sera prima</strong><small>Una prenotazione fatta, ad esempio, alle 2 di notte comparirà nell'agenda del giorno precedente invece che in quello nuovo appena iniziato.</small></span></label>{config.settings.nightCutoffEnabled && <label>Fino a che ora del mattino<select value={config.settings.nightCutoffHour} onChange={(event) => setConfig({ ...config, settings: { ...config.settings, nightCutoffHour: Number(event.target.value) } })}>{Array.from({ length: 13 }, (_, hour) => hour).map((hour) => <option key={hour} value={hour}>{String(hour).padStart(2, "0")}:00</option>)}</select><small>Le prenotazioni prima di quest'ora vengono contate nella serata precedente.</small></label>}<label>Telefono WhatsApp del locale<input value={config.settings.restaurantPhone} onChange={(event) => setConfig({ ...config, settings: { ...config.settings, restaurantPhone: event.target.value } })} placeholder="+393…" /></label><label>URL informativa privacy<input type="url" value={config.settings.privacyUrl} onChange={(event) => setConfig({ ...config, settings: { ...config.settings, privacyUrl: event.target.value } })} /></label></section>
        <section><h3>Orari e limiti per fascia</h3>{config.hours.map((hour) => <div className="service-hour" key={hour.id}><label><input type="checkbox" checked={hour.enabled} onChange={(event) => setConfig({ ...config, hours: config.hours.map((item) => item.id === hour.id ? { ...item, enabled: event.target.checked } : item) })} />{weekdays[hour.weekday]}</label><input aria-label="Apertura" type="time" value={hour.openTime} onChange={(event) => setConfig({ ...config, hours: config.hours.map((item) => item.id === hour.id ? { ...item, openTime: event.target.value } : item) })} /><input aria-label="Chiusura" type="time" value={hour.closeTime} onChange={(event) => setConfig({ ...config, hours: config.hours.map((item) => item.id === hour.id ? { ...item, closeTime: event.target.value } : item) })} /><label>Coperti<input type="number" min="1" max="5000" value={hour.maxCoversPerSlot} onChange={(event) => setConfig({ ...config, hours: config.hours.map((item) => item.id === hour.id ? { ...item, maxCoversPerSlot: Number(event.target.value) } : item) })} /></label></div>)}</section>
        <section className="messaging-settings">
          <div className="messaging-settings-head"><div><p className="eyebrow indigo">Avvisi allo staff</p><h3>Notifiche Telegram</h3><p>I collaboratori registrati ricevono subito cliente, data, ora, persone e momento esatto in cui la richiesta è arrivata.</p></div><span className={`messaging-config-state ${messagingConfig.telegramEnabled && messagingConfig.telegramBotTokenConfigured ? "ready" : "pending"}`}><i />{messagingConfig.telegramEnabled && messagingConfig.telegramBotTokenConfigured ? `${messagingConfig.telegramChatIds.length} destinatari` : "Da configurare"}</span></div>
          <div className="messaging-setup-steps"><span><b>1</b>Crea il bot con BotFather</span><span><b>2</b>Avvia il bot da ogni account</span><span><b>3</b>Inserisci i Chat ID</span></div>
          <label className="booking-enable"><input type="checkbox" checked={messagingConfig.telegramEnabled} disabled={!canEdit} onChange={(event) => setMessagingConfig({ ...messagingConfig, telegramEnabled: event.target.checked })} /><span><strong>Notifiche nuove prenotazioni</strong><small>Ogni prenotazione web genera un messaggio separato a tutti i Chat ID configurati.</small></span></label>
          <div className="field-pair"><label>Token bot Telegram<input type="password" value={messagingSecrets.telegramBotToken} disabled={!canEdit} autoComplete="new-password" onChange={(event) => setMessagingSecrets({ ...messagingSecrets, telegramBotToken: event.target.value })} placeholder={messagingConfig.telegramBotTokenConfigured ? "Token configurato, inseriscine uno nuovo per sostituirlo" : "123456:ABC…"} /></label><label>Chat ID, uno per riga<textarea value={messagingConfig.telegramChatIds.join("\n")} disabled={!canEdit} onChange={(event) => setMessagingConfig({ ...messagingConfig, telegramChatIds: event.target.value.split(/\s+/).filter(Boolean) })} placeholder={"123456789\n987654321"} /></label></div>
          <div className="telegram-discovery"><div><strong>Aggiungi chi ha scritto al bot</strong><p>Telegram usa un Chat ID, non il numero di telefono. Premi rileva e scegli la persona dal nome.</p></div><button className="secondary" onClick={discoverTelegramRecipients} disabled={!canEdit || !messagingConfig.telegramBotTokenConfigured || busy === "telegram-discover"}>{busy === "telegram-discover" ? "Ricerca…" : "Rileva nuovi contatti"}</button></div>
          {telegramCandidates.length > 0 && <div className="telegram-candidates">{telegramCandidates.map((candidate) => <article key={candidate.chatId}><div><strong>{candidate.name}</strong><span>{candidate.username ? `@${candidate.username} · ` : ""}{candidate.lastMessageAt ? new Date(candidate.lastMessageAt).toLocaleString("it-IT") : "Messaggio ricevuto"}</span></div><button className="secondary" onClick={() => registerTelegramRecipient(candidate)} disabled={!canEdit || candidate.configured || busy === `telegram-register-${candidate.chatId}`}>{candidate.configured ? "Già registrato" : busy === `telegram-register-${candidate.chatId}` ? "Aggiunta…" : "Aggiungi e prova"}</button></article>)}</div>}
          <div className="messaging-test-row"><button className="secondary" onClick={sendTelegramTest} disabled={!canEdit || !messagingConfig.telegramBotTokenConfigured || !messagingConfig.telegramChatIds.length || busy === "telegram-test"}>{busy === "telegram-test" ? "Invio…" : "Invia prova Telegram"}</button>{messagingConfig.telegramLastTestAt && <small>Ultimo test: {new Date(messagingConfig.telegramLastTestAt).toLocaleString("it-IT")}</small>}</div>
        </section>
        <section className="messaging-settings whatsapp-settings">
          <div className="messaging-settings-head"><div><p className="eyebrow indigo">Messaggi al cliente</p><h3>WhatsApp Business Platform</h3><p>Usa la Cloud API ufficiale Meta, consenso esplicito e template utility approvati. Nessuna automazione tramite WhatsApp Web.</p></div><span className={`messaging-config-state ${whatsappIntegration.connected ? "ready" : "pending"}`}><i />{whatsappIntegration.connected ? "Collegata a Meta" : "Da collegare"}</span></div>
          <div className="meta-connect-card"><div><strong>{whatsappIntegration.connected ? (whatsappIntegration.verifiedName || "Account WhatsApp collegato") : "Collega il numero con Meta"}</strong><p>{whatsappIntegration.connected ? `${whatsappIntegration.displayPhoneNumber || "Numero verificato"} · WABA ${whatsappIntegration.wabaId}` : "Si apre la procedura ufficiale Meta. Il token viene salvato cifrato e assegnato soltanto a questo sito."}</p></div><button className="primary" type="button" onClick={connectWhatsappWithMeta} disabled={!canEdit || !whatsappIntegration.configured || busy === "whatsapp-connect"}>{busy === "whatsapp-connect" ? "Collegamento…" : whatsappIntegration.connected ? "Ricollega con Meta" : "Collega con Meta"}</button></div>
          {whatsappIntegration.connected && whatsappIntegration.webhooksSubscribed === false && (
            <div className="notice error">Numero collegato, ma Meta non ha ancora attivato gli aggiornamenti (stato dei messaggi, scollegamento).{" "}
              <button type="button" onClick={retryWhatsappSync} disabled={!canEdit || busy === "whatsapp-sync"}>{busy === "whatsapp-sync" ? "Riprovo…" : "Riprova"}</button>
            </div>
          )}
          {whatsappIntegration.connected && whatsappIntegration.onboarding === "coexistence" && whatsappIntegration.coexistence && (
            <div className={`notice ${whatsappIntegration.coexistence.syncComplete ? "success" : "error"}`}>
              <strong>Numero usato anche nell'app WhatsApp Business (coesistenza).</strong>{" "}
              {whatsappIntegration.coexistence.syncComplete
                ? "Contatti e storico: sincronizzazione richiesta a Meta."
                : whatsappIntegration.coexistence.syncExpired
                  ? "Sincronizzazione non completata entro 24 ore: Meta richiede di scollegare il numero dall'app (Impostazioni → Account → Business Platform) e ricollegarlo."
                  : `Sincronizzazione incompleta${whatsappIntegration.coexistence.lastError ? `: ${whatsappIntegration.coexistence.lastError}` : ""}. Va completata entro 24 ore dal collegamento.`}
              {!whatsappIntegration.coexistence.syncComplete && !whatsappIntegration.coexistence.syncExpired && (
                <button type="button" onClick={retryWhatsappSync} disabled={!canEdit || busy === "whatsapp-sync"}>{busy === "whatsapp-sync" ? "Sincronizzazione…" : "Riprova sincronizzazione"}</button>
              )}
            </div>
          )}
          {!whatsappIntegration.configured && <div className="notice error">L'app Meta di Universe non è ancora configurata completamente sul server.</div>}
          <label className="booking-enable"><input type="checkbox" checked={messagingConfig.whatsappEnabled} disabled={!canEdit} onChange={(event) => setMessagingConfig({ ...messagingConfig, whatsappEnabled: event.target.checked })} /><span><strong>Messaggi automatici WhatsApp</strong><small>Richiesta ricevuta, conferma e cancellazione partono per chi ha lasciato il numero nel modulo, dove è informato che riceverà messaggi di servizio su WhatsApp.</small></span></label>
          <div className="whatsapp-pricing"><div><strong>€ 0,0248</strong><span>messaggio utility consegnato in Italia</span></div><p>Stima diretta Meta, primo scaglione: due messaggi per 100 prenotazioni costano circa € 4,96. I messaggi di servizio nella finestra di assistenza sono gratuiti; i messaggi marketing costano € 0,0658. Importi Meta verificati l’11 settembre 2026, prima di eventuali IVA o costi di un fornitore esterno.</p><a href="https://whatsappbusiness.com/products/platform-pricing/" target="_blank" rel="noreferrer">Controlla il listino ufficiale Meta</a></div>
          <details className="advanced-credentials"><summary>Configurazione manuale avanzata</summary><div className="field-pair"><label>Token permanente Meta<input type="password" value={messagingSecrets.whatsappAccessToken} disabled={!canEdit} autoComplete="new-password" onChange={(event) => setMessagingSecrets({ ...messagingSecrets, whatsappAccessToken: event.target.value })} placeholder={messagingConfig.whatsappAccessTokenConfigured ? "Token configurato, inseriscine uno nuovo per sostituirlo" : "EAAG…"} /></label><label>Phone Number ID<input inputMode="numeric" value={messagingConfig.whatsappPhoneNumberId} disabled={!canEdit} onChange={(event) => setMessagingConfig({ ...messagingConfig, whatsappPhoneNumberId: event.target.value.replace(/\D/g, "") })} placeholder="123456789012345" /></label></div></details>
          <div className="template-grid"><label>Template richiesta ricevuta<input value={messagingConfig.whatsappTemplateReceived} disabled={!canEdit} onChange={(event) => setMessagingConfig({ ...messagingConfig, whatsappTemplateReceived: event.target.value })} /></label><label>Template confermata<input value={messagingConfig.whatsappTemplateConfirmed} disabled={!canEdit} onChange={(event) => setMessagingConfig({ ...messagingConfig, whatsappTemplateConfirmed: event.target.value })} /></label><label>Template cancellata<input value={messagingConfig.whatsappTemplateCancelled} disabled={!canEdit} onChange={(event) => setMessagingConfig({ ...messagingConfig, whatsappTemplateCancelled: event.target.value })} /></label><label>Template lista d'attesa<input value={messagingConfig.whatsappTemplateWaitlisted} disabled={!canEdit} onChange={(event) => setMessagingConfig({ ...messagingConfig, whatsappTemplateWaitlisted: event.target.value })} /></label><label>Template promemoria<input value={messagingConfig.whatsappTemplateReminder} disabled={!canEdit} onChange={(event) => setMessagingConfig({ ...messagingConfig, whatsappTemplateReminder: event.target.value })} /></label><label>Lingua<input value={messagingConfig.whatsappTemplateLanguage} disabled={!canEdit} onChange={(event) => setMessagingConfig({ ...messagingConfig, whatsappTemplateLanguage: event.target.value })} /></label></div>
          <label className="booking-enable"><input type="checkbox" checked={messagingConfig.whatsappReminderEnabled} disabled={!canEdit} onChange={(event) => setMessagingConfig({ ...messagingConfig, whatsappReminderEnabled: event.target.checked })} /><span><strong>Promemoria WhatsApp prima della prenotazione</strong><small>Solo prenotazioni confermate con consenso WhatsApp. Attivalo dopo che Meta ha approvato il template promemoria. Non parte se la prenotazione è stata fatta poco prima.</small></span></label>
          <label className="reminder-hours">Ore di anticipo del promemoria<input type="number" min={1} max={24} value={messagingConfig.whatsappReminderHours} disabled={!canEdit || !messagingConfig.whatsappReminderEnabled} onChange={(event) => { const hours = Math.round(Number(event.target.value)); if (hours >= 1 && hours <= 24) setMessagingConfig({ ...messagingConfig, whatsappReminderHours: hours }); }} /></label>
          <label className="booking-enable"><input type="checkbox" checked={messagingConfig.whatsappManageButtonEnabled} disabled={!canEdit} onChange={(event) => setMessagingConfig({ ...messagingConfig, whatsappManageButtonEnabled: event.target.checked })} /><span><strong>Pulsante “Gestisci o annulla” nella conferma</strong><small>Attivalo solo dopo che Meta ha approvato nel template confermato il primo pulsante URL dinamico: https://www.narcisolounge.com/prenota#booking={"{{1}}"}</small></span></label>
          <div className="messaging-safety"><strong>Protezione anti-spam</strong><p>I template devono essere approvati da Meta e contenere, nello stesso ordine: nome, data, ora, persone e servizio (la conferma di rifiuto solo nome, data e ora). I messaggi di servizio su WhatsApp sono informati nel modulo (base contrattuale); le promozioni partono solo con il consenso marketing, separato.</p></div>
          <div className="messaging-test-row"><button className="secondary" onClick={testWhatsappConnection} disabled={!canEdit || !messagingConfig.whatsappAccessTokenConfigured || !messagingConfig.whatsappPhoneNumberId || busy === "whatsapp-test"}>{busy === "whatsapp-test" ? "Verifica…" : "Verifica connessione Meta"}</button>{messagingConfig.whatsappLastTestAt && <small>Ultima verifica: {new Date(messagingConfig.whatsappLastTestAt).toLocaleString("it-IT")}</small>}</div>
        </section>
        <button className="primary" onClick={saveMessagingConfig} disabled={!canEdit || busy === "messaging-config"}>{busy === "messaging-config" ? "Salvataggio…" : "Salva notifiche e WhatsApp"}</button>
        <section className="integration-roadmap"><h3>Reserve with Google</h3><span className={`google-state state-${config.settings.googleReserveStatus}`}>{config.settings.googleReserveStatus === "live" ? "Attivo" : "Da attivare come partner"}</span><p>Il booking server e l’inventario sono predisposti. Per mostrare il pulsante nella scheda Google Universe deve essere ammessa al programma Actions Center, completare sandbox, feed giornalieri e revisione produzione.</p></section>
        <button className="primary" onClick={() => saveConfig()} disabled={!canEdit || busy === "config"}>{busy === "config" ? "Salvataggio…" : "Salva impostazioni"}</button>
      </div>}
    </div>
  );
}
